import {
  CapTableOrdering,
  Column,
  defaultCapTableOrdering,
  DetailsSortVariant,
  PoolStats,
  Snapshot,
  ViewBy,
} from '../../../../types/snapshot.types';
import { calcTotal } from '../../../../utils/reduce-utils.util';
import { toNumber } from '../../../../utils/to-number.util';

export const getPoolDilutedUnvested = (pool: PoolStats): number =>
  toNumber(pool.dilutedUnvested ?? Math.max(0, pool.unvested));

export const getEquityPoolsGrantedDilutedShares = ({
  dilutedTotal,
  dilutedInOtherRows,
  grantablePoolDiluted,
}: {
  dilutedInOtherRows: number;
  dilutedTotal: number;
  grantablePoolDiluted: number;
}): number =>
  Math.max(
    0,
    toNumber(dilutedTotal) - toNumber(dilutedInOtherRows) - toNumber(grantablePoolDiluted),
  );

export const getPoolGrantedRowDilutedShares = (
  pool: PoolStats,
  equityPoolsGrantedDilutedShares: number,
  equityPoolGrantable: PoolStats[],
): number => {
  if (equityPoolsGrantedDilutedShares <= 0) {
    return 0;
  }

  const weightTotal = calcTotal(equityPoolGrantable, getPoolDilutedUnvested);
  if (weightTotal <= 0) {
    return 0;
  }

  return (getPoolDilutedUnvested(pool) / weightTotal) * equityPoolsGrantedDilutedShares;
};

export const getPoolShareClassAmountTotal = (
  pools: PoolStats[],
  shareClassId: string,
  amountKey: 'issued' | 'unvested',
): number =>
  calcTotal(
    pools.filter((pool) => pool.shareClassId === shareClassId),
    (pool) => toNumber(pool[amountKey]),
  );

export const getShareClasses = <T extends { id: string }>(
  defaultShareClassId: string,
  arr: T[],
) => ({
  defaultShareClass: arr.find(({ id }) => id === defaultShareClassId),
  otherShareClasses: arr.filter(({ id }) => id !== defaultShareClassId),
});

/**
 * What these views actually read from a snapshot. Stating it lets the public shared page pass its
 * narrower sanitised payload without a cast, while a full `Snapshot` still satisfies it. If a view
 * starts reading another field, this type has to grow — which is the point.
 */
type RenderableSnapshotStats = Pick<
  Snapshot['stats'],
  'pools' | 'shareClasses' | 'sharePlans' | 'stakeholders' | 'stakeholdersTypes'
>;

export type RenderableSnapshot = {
  stats: RenderableSnapshotStats;
} & Pick<Snapshot, 'dilutedShares' | 'issuedShares'>;

// Equity pool and total rows render regardless of stakeholder matches (a pool-only company has
// no stakeholder rows but still has a table), so zero matches means "no results" only while a
// search is narrowing the table.
export const isSearchWithoutMatches = (search: string, matchedRowCount: number): boolean =>
  search.trim().length > 0 && matchedRowCount === 0;

/**
 * A single stable instance: the views feed `ordering` to `useMemo`, so a fresh object per render
 * would re-sort every row on every render.
 */
export const DEFAULT_CAP_TABLE_ORDERING: CapTableOrdering = defaultCapTableOrdering();

export type SnapshotViewByProps = {
  /**
   * Supplied by the public shared page, which has no session to resolve the default share class
   * with. When present the authorised lookup is skipped; authenticated callers omit it.
   */
  defaultShareClassId?: string;
  /**
   * Omitted by the public shared page, which renders a link's fixed view and has no Customize View
   * modal of its own — it gets the standing default ordering.
   */
  ordering?: CapTableOrdering;
  possibleShareClasses: string[];
  search: string;
  setEmptySearchResult: (state: boolean) => void;
  snapshot: RenderableSnapshot;
  type: DetailsSortVariant;
  viewBy: Record<ViewBy, boolean>;
  visibleColumns: Column[];
};
